import { useEffect, useRef, useState } from "react"
import nProgress from "nprogress"

import useCustomerPickerRepositories from "repositories/customer-picker"

export default function CustomerPickerModal({
  open,
  onClose,
  onSelect,
}) {
  const searchInput = useRef(null)
  const debounceRef = useRef(null)

  const [search, setSearch] = useState("")
  const [committedSearch, setCommittedSearch] = useState("")
  const [page, setPage] = useState(1)

  const { data, isLoading } = useCustomerPickerRepositories(
    {
      page,
      search: committedSearch || undefined,
      pagination:true
    },
    {
      enabled: open,
    }
  )

  const customerData = data ?? {}

  useEffect(() => {
    if (!open) return

    setSearch("")
    setCommittedSearch("")
    setPage(1)
  }, [open])

  useEffect(() => {
    if (!open) return
    searchInput.current?.focus()
  }, [open])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  if (!open) return null

  const rows = customerData?.data ?? []
  const currentPage = customerData?.current_page ?? page
  const lastPage = customerData?.last_page ?? 1
  const from = customerData?.from ?? 0
  const to = customerData?.to ?? 0
  const total = customerData?.total ?? 0

  const updateSearch = (value) => {
    setSearch(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      setCommittedSearch(value)
      setPage(1)
    }, 400)
  }

  const commitSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setCommittedSearch(search)
    setPage(1)
  }

  const clearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSearch("")
    setCommittedSearch("")
    setPage(1)
    searchInput.current?.focus()
  }

  const getCustomerName = (v) => {
    return (
      v.full_name ||
      v.name ||
      v.customer_name ||
      "-"
    )
  }

  const getCustomerPhone = (v) => {
    return (
      v.phone_number ||
      v.phone ||
      v.mobile_phone ||
      "-"
    )
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Pilih Customer
            </h3>
            <p className="text-sm text-neutral-500">
              Cari berdasarkan nama atau nomor telepon
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="relative">
            <input
              ref={searchInput}
              value={search}
              onChange={(e) => updateSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitSearch()
                }
              }}
              type="text"
              placeholder="Cari nama atau nomor telepon..."
              className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
            />

            {!!search && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-700"
              >
                ✕
              </button>
            )}
          </div>

          <div className="border rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-semibold border-b bg-neutral-50 text-neutral-600">
              <div className="col-span-1">ID</div>
              <div className="col-span-5">Nama</div>
              <div className="col-span-4">Phone</div>
              <div className="col-span-2 text-right">Aksi</div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Memuat data customer...
                </div>
              ) : !rows.length ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Customer tidak ditemukan.
                </div>
              ) : (
                rows.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-12 gap-4 px-4 py-4 border-b last:border-b-0 items-center"
                  >
                    <div className="col-span-1 text-sm text-neutral-600">
                      {item.id}
                    </div>

                    <div className="col-span-5">
                      <div className="font-medium text-neutral-800">
                        {getCustomerName(item)}
                      </div>

                      {!!item.email && (
                        <div className="text-xs text-neutral-500">
                          {item.email}
                        </div>
                      )}
                    </div>

                    <div className="col-span-4 text-sm text-neutral-700">
                      {getCustomerPhone(item)}
                    </div>

                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          onSelect({
                            id: item.id,
                            name: getCustomerName(item),
                            phone: getCustomerPhone(item),
                            raw: item,
                          })
                        }
                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700"
                      >
                        Pilih
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-neutral-500">
              {total > 0
                ? `Menampilkan ${from}-${to} dari ${total} customer`
                : "Tidak ada data"}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="px-4 py-2 text-sm border rounded-xl border-neutral-200 disabled:opacity-50"
              >
                Prev
              </button>

              <div className="px-3 text-sm text-neutral-600">
                {currentPage} / {lastPage}
              </div>

              <button
                type="button"
                disabled={currentPage >= lastPage}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-4 py-2 text-sm border rounded-xl border-neutral-200 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}